import {
    FC,
    FormEvent,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';

import { ErrorLabel } from '../../../components';
import {
    Button,
    Checkbox,
    PasswordInput,
    RecaptchaDisclaimer,
    TextInput,
} from '../../../compositions';
import { FormProps } from '../../../entities/@forms/Form/Form';
import { LoginFormData } from '../../../entities/@forms/LoginForm/LoginForm';
import { AppRoute, appRoutes } from '../../../entities/Routing/Routing';
import { scrollIntoViewIncludingNavigation } from '../../../helpers/scroll';
import { useTrans } from '../../../hooks';
import { LoginFormErrors, validateLoginFormData } from './validations';

import './LoginForm.scss';

interface LoginFormProps extends FormProps<LoginFormData> {
    heading?: string;
    emailAddress?: string;
    className?: string;
}

export const LoginForm: FC<LoginFormProps> = ({
    isLoading,
    error,
    onSubmit,
    heading,
    emailAddress = '',
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formRef = useRef<HTMLFormElement>(null);
    const [formErrors, setFormErrors] = useState<LoginFormErrors>({});
    const [botDetected, setBotDetected] = useState<boolean>(false);

    const [email, setEmail] = useState<string>(emailAddress);
    const [password, setPassword] = useState<string>('');
    const [rememberMe, setRememberMe] = useState<boolean>(true);

    useEffect((): void => {
        if (emailAddress) {
            setEmail(emailAddress);
        }
    }, [emailAddress]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!executeRecaptcha) {
            setBotDetected(true);
            return;
        }

        const formData: LoginFormData = {
            email,
            password,
            rememberMe,
        };

        const [errors, hasErrors] = validateLoginFormData(formData);
        const token = await executeRecaptcha();

        setFormErrors(errors);
        setBotDetected(!token);

        if (!hasErrors) {
            onSubmit(formData);
        } else {
            scrollIntoViewIncludingNavigation<HTMLFormElement>(formRef);
        }
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`login-form ${className}`}
        >
            {heading && (
                <h1 className="login-form__heading">
                    {heading}
                </h1>
            )}

            <TextInput
                type="email"
                inputMode="email"
                autoComplete="email"
                label={trans('containers.loginForm.emailLabel')}
                placeholder={trans('containers.loginForm.emailPlaceholder')}
                value={email}
                error={formErrors.email}
                disabled={isLoading}
                onChange={setEmail}
                className="login-form__username-input"
            />

            <PasswordInput
                autoComplete="current-password"
                label={trans('containers.loginForm.passwordLabel')}
                placeholder={trans('containers.loginForm.passwordPlaceholder')}
                value={password}
                error={formErrors.password}
                disabled={isLoading}
                onChange={setPassword}
                className="login-form__password-input"
                fieldClassName="login-form__password-input-field"
            />

            {error && (
                <ErrorLabel
                    text={error}
                    className="login-form__error-label"
                />
            )}

            <Checkbox
                label={trans('containers.loginForm.rememberMeLabel')}
                checked={rememberMe}
                disabled={isLoading}
                onChange={setRememberMe}
                className="login-form__remember-me-checkbox"
            />

            <Button
                hasAnimation
                type="submit"
                disabled={isLoading}
                text={trans('containers.loginForm.submitButton')}
                className="login-form__submit-button"
            />

            <RecaptchaDisclaimer className="login-form__recaptcha-disclaimer" />

            {botDetected && (
                <ErrorLabel text={trans('errors.unknownError')} />
            )}

            <Link
                to={trans(appRoutes[AppRoute.requestResetPassword].path)}
                className="login-form__reset-password-button"
            >
                {trans('containers.loginForm.resetPasswordButton')}
            </Link>
        </form>
    );
};
